/**
 * Clarify step — quick multiple-choice questions between the prompt and the
 * generation (Claude-Code-style question card). Single-select renders as a
 * pill radio row, multi-select as toggleable chips; `allowOther` adds an
 * "Other…" chip with a free-text input. Entirely optional: Skip generates
 * with the prompt as-is.
 */

import React, { useState } from 'react'
import { FaArrowRight } from 'react-icons/fa'
import type { ClarifyQuestion, Clarification } from '../onboardingApi'
import { useCopy } from '../regionCopy'

const OTHER_KEY = '__other__'

interface ClarifyCardProps {
  questions: ClarifyQuestion[]
  busy: boolean
  onSubmit: (clarifications: Clarification[]) => void
  onSkip: () => void
}

interface AnswerState {
  selected: Set<string>
  otherText: string
}

export const ClarifyCard: React.FC<ClarifyCardProps> = ({ questions, busy, onSubmit, onSkip }) => {
  const [answers, setAnswers] = useState<Record<string, AnswerState>>({})
  const c = useCopy()

  const stateFor = (id: string): AnswerState =>
    answers[id] ?? { selected: new Set<string>(), otherText: '' }

  const toggle = (q: ClarifyQuestion, option: string): void => {
    setAnswers((prev) => {
      const cur = stateFor(q.id)
      const next = new Set(cur.selected)
      if (q.type === 'single') {
        const wasOn = next.has(option)
        next.clear()
        if (!wasOn) next.add(option)
      } else if (next.has(option)) {
        next.delete(option)
      } else {
        next.add(option)
      }
      return { ...prev, [q.id]: { ...cur, selected: next } }
    })
  }

  const setOther = (q: ClarifyQuestion, text: string): void => {
    setAnswers((prev) => {
      const cur = stateFor(q.id)
      return { ...prev, [q.id]: { ...cur, otherText: text } }
    })
  }

  const collect = (): Clarification[] => {
    const out: Clarification[] = []
    for (const q of questions) {
      const st = stateFor(q.id)
      const parts = q.options.filter((o) => st.selected.has(o))
      if (st.selected.has(OTHER_KEY) && st.otherText.trim()) {
        parts.push(st.otherText.trim())
      }
      if (parts.length > 0) out.push({ question: q.question, answer: parts.join(', ') })
    }
    return out
  }

  const answeredCount = questions.filter((q) => {
    const st = stateFor(q.id)
    return (
      q.options.some((o) => st.selected.has(o)) ||
      (st.selected.has(OTHER_KEY) && st.otherText.trim().length > 0)
    )
  }).length

  return (
    <div className="relative mx-auto flex w-full max-w-2xl flex-col gap-6 px-4 py-10 sm:px-6 sm:py-14">
      <header className="space-y-2 text-center">
        <h2 className="text-2xl font-bold sm:text-3xl">
          <span className="bg-gradient-to-r from-purpleSnail to-luridRed bg-clip-text text-transparent">
            {c.quickQuestions}
          </span>
        </h2>
        <p className="mx-auto max-w-md text-sm text-whiteout/60">
          {c.quickQuestionsSub}
        </p>
      </header>

      <div className="space-y-5">
        {questions.map((q) => {
          const st = stateFor(q.id)
          const showOther = q.allowOther
          const otherOn = st.selected.has(OTHER_KEY)
          return (
            <div key={q.id} className="rounded-2xl border border-white/10 bg-white/[0.04] p-4">
              <p className="mb-3 text-sm font-medium text-whiteout/90">{q.question}</p>
              <div
                className="flex flex-wrap gap-2"
                role={q.type === 'single' ? 'radiogroup' : 'group'}
              >
                {q.options.map((o) => {
                  const on = st.selected.has(o)
                  return (
                    <button
                      key={o}
                      type="button"
                      role={q.type === 'single' ? 'radio' : 'checkbox'}
                      aria-checked={on}
                      onClick={() => toggle(q, o)}
                      className={`rounded-full border px-3.5 py-1.5 text-xs transition-colors ${
                        on
                          ? 'border-blueSparkle bg-blueSparkle/20 text-whiteout'
                          : 'border-white/10 text-whiteout/60 hover:border-white/30 hover:text-whiteout'
                      }`}
                    >
                      {o}
                    </button>
                  )
                })}
                {showOther && (
                  <button
                    type="button"
                    role={q.type === 'single' ? 'radio' : 'checkbox'}
                    aria-checked={otherOn}
                    onClick={() => toggle(q, OTHER_KEY)}
                    className={`rounded-full border px-3.5 py-1.5 text-xs transition-colors ${
                      otherOn
                        ? 'border-blueSparkle bg-blueSparkle/20 text-whiteout'
                        : 'border-dashed border-white/15 text-whiteout/50 hover:border-white/30 hover:text-whiteout'
                    }`}
                  >
                    {c.other}
                  </button>
                )}
              </div>
              {showOther && otherOn && (
                <input
                  value={st.otherText}
                  onChange={(e) => setOther(q, e.target.value)}
                  maxLength={200}
                  autoFocus
                  placeholder={c.otherPlaceholder}
                  className="mt-3 w-full rounded-xl border border-white/15 bg-transparent px-3 py-2 text-sm text-whiteout placeholder-whiteout/30 outline-none focus:border-blueSparkle/70"
                />
              )}
            </div>
          )
        })}
      </div>

      <div className="flex flex-col-reverse items-stretch gap-3 sm:flex-row sm:items-center sm:justify-between">
        <button
          type="button"
          onClick={onSkip}
          disabled={busy}
          className="rounded-full px-4 py-2.5 text-sm text-whiteout/50 transition-colors hover:text-whiteout disabled:opacity-40"
        >
          {c.skipJustGenerate}
        </button>
        <button
          type="button"
          onClick={() => onSubmit(collect())}
          disabled={busy}
          className="inline-flex items-center justify-center gap-2 rounded-full bg-blueSparkle px-6 py-2.5 text-sm font-semibold text-whiteout shadow-lg transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
        >
          {answeredCount > 0 ? c.buildWithAnswers(answeredCount) : c.continueLabel}
          <FaArrowRight className="text-xs" />
        </button>
      </div>
    </div>
  )
}

export default ClarifyCard
